import { addProtocol } from 'maplibre-gl';

/**
 * Scheme a URL is prefixed with to have its tile fetched through {@link retryingTransformRequest}
 * instead of MapLibre's own loader.
 */
const protocol = 'geovelo-retry';

/**
 * Statuses worth asking again for.
 *
 * `400` does not belong in a list like this and is here on purpose. The IGN WMTS endpoint answers a
 * handful of tiles with it under the load of a video rendering — a thousand requests in a couple of
 * minutes — and the very same tiles come back `200` on a direct request a moment later, up to z19.
 * Whatever it is, it is not a malformed request, and treating it as permanent is what turns it into
 * a hole in the video.
 */
const retriableStatuses = new Set([400, 408, 425, 429, 500, 502, 503, 504]);
const maxAttempts = 3;
const backoffInMilliseconds = 150;
let registered = false;
let source;

/**
 * Both families of Geovelo tile hosts: `tiles*` serves the vector tiles (base map, labels,
 * facilities, POIs) and `tileserver*` the rest (hillshades, contours, parkings, ...).
 */
const geoveloTileHosts = /^https:\/\/(tiles|tileserver)\d*(-dev)?\.geovelo\.fr\//;
function withSource(url) {
  if (!source || !geoveloTileHosts.test(url)) return url;
  const tagged = new URL(url);
  tagged.searchParams.set('source', source);
  return tagged.toString();
}
async function fetchWithRetry(url, signal) {
  let lastStatus = 0;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const response = await fetch(withSource(url), {
      signal,
      credentials: 'same-origin'
    });
    if (response.ok) return response.arrayBuffer();
    lastStatus = response.status;
    if (!retriableStatuses.has(response.status) || attempt === maxAttempts) break;
    await new Promise(resolve => {
      window.setTimeout(resolve, backoffInMilliseconds * attempt);
    });
  }
  throw new Error(`tile request failed with ${lastStatus}`);
}

/**
 * Teaches MapLibre to ask a second time for a tile the server refused.
 *
 * It otherwise never does: a tile whose request fails is marked `errored`, and `errored` counts as
 * finished everywhere it matters — `areTilesLoaded` returns true with one in view, and the tile is
 * never requested again for the life of the map. During a video that is unrecoverable: a single
 * refusal lasting a millisecond becomes a blank square for as long as that ground is on screen.
 *
 * Registered once, globally, because `addProtocol` is global. Nothing routes through it until a map
 * opts in with {@link retryingTransformRequest}.
 *
 * With `options.source`, tiles fetched from the Geovelo tile hosts carry it as their `source`
 * query parameter. As the registration is global, so is that setting: the last call wins.
 */
export function registerTileRetryProtocol(options) {
  if (options) source = options.source;
  if (registered || typeof window === 'undefined') return;
  registered = true;
  addProtocol(protocol, async (params, abortController) => ({
    data: await fetchWithRetry(params.url.replace(`${protocol}://`, ''), abortController.signal)
  }));
}

/**
 * A `transformRequest` that routes tile loads through the retrying protocol.
 *
 * Scoped to tiles: the style document, sprites and glyphs are fetched once and a failure there is
 * loud and obvious, where a tile failing is silent and leaves a hole.
 */
export function retryingTransformRequest(url, resourceType) {
  if (resourceType !== 'Tile' || !/^https?:\/\//.test(url)) return undefined;
  return {
    url: `${protocol}://${url}`
  };
}